/**
 * @fileoverview Review System Hooks
 * 
 * Manages the two-party review system where clients and vendors review each other
 * after job completion. Implements a "blind review" mechanism where reviews are
 * hidden until both parties submit, then revealed simultaneously.
 * 
 * **Review Workflow:**
 * 1. Job completes successfully
 * 2. Both parties submit reviews (status: 'pending_reveal')
 * 3. Once both submitted, reviews auto-publish (status: 'published')
 * 4. Reviews become visible on user profiles
 * 
 * **Review Metrics:**
 * - Overall rating (1-5 stars)
 * - Quality, communication, timeliness, professionalism ratings
 * - Optional text comments
 * - Aggregate statistics per user
 * 
 * @module hooks/useReviews
 */

import { useState, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { DatabaseReview, Review, ReviewSubmission, UserReviewAggregate } from '@/lib/types/review';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

// Type conversion helper
const convertDatabaseReview = (dbReview: DatabaseReview): Review => ({
  ...dbReview,
  reviewer_type: dbReview.reviewer_type as 'client' | 'vendor',
  review_status: dbReview.review_status as 'draft' | 'pending_reveal' | 'published'
});

export type { Review, ReviewSubmission, UserReviewAggregate };

// Module-level in-flight request caches to prevent duplicate concurrent requests
const inFlightUserReviews = new Map<string, Promise<Review[]>>();
const inFlightUserAggregates = new Map<string, Promise<UserReviewAggregate | null>>();

/**
 * Hook for managing the review system (client ↔ vendor reviews)
 * 
 * Handles review submission, fetching, and publishing. Reviews follow a two-party
 * reveal system where both parties must submit before either can see the other's review.
 * 
 * **Key Functions:**
 * - `submitReview`: Submit a new review for a job
 * - `canUserReviewJob`: Check if user is eligible to review
 * - `getUserReviews`: Get all published reviews for a user
 * - `getUserReviewAggregate`: Get aggregate stats (avg rating, count)
 * - `updateReview`: Modify an existing review (before publishing)
 * - `checkPendingReviews`: Find reviews waiting for counterpart
 * 
 * **Review Eligibility:**
 * - Must be associated with the job (as client or vendor)
 * - Job must be completed
 * - Cannot review same job twice from same role
 * 
 * @returns Object with review management functions and loading states
 * 
 * @example
 * ```typescript
 * const { submitReview, getUserReviews, canUserReviewJob } = useReviews();
 * 
 * // Check if user can review a job
 * const canReview = await canUserReviewJob(jobId, 'client');
 * 
 * // Submit a review
 * await submitReview(jobId, vendorId, 'client', orgId, {
 *   rating: 5,
 *   quality_rating: 5,
 *   communication_rating: 5,
 *   timeliness_rating: 5,
 *   professionalism_rating: 4,
 *   comment: 'Great work!'
 * });
 * ```
 */
export const useReviews = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  /**
   * Check if the current user is eligible to review a specific job
   */
  const canUserReviewJob = useCallback(async (jobId: string, reviewerType: 'client' | 'vendor'): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('can_user_review_job', {
        job_uuid: jobId,
        reviewer_type: reviewerType
      });

      if (error) throw error;
      return data || false;
    } catch (error) {
      console.error('Error checking review eligibility:', error);
      return false;
    }
  }, []);

  /**
   * Submit a new review for a job
   */
  const submitReview = useCallback(async (
    jobId: string,
    revieweeUserId: string,
    reviewerType: 'client' | 'vendor',
    organizationId: string,
    reviewData: ReviewSubmission
  ): Promise<boolean> => {
    setSubmitting(true);
    try {
      // Validate required fields
      if (!revieweeUserId || revieweeUserId.trim() === '') {
        throw new Error('Cannot submit review: reviewee user ID is missing');
      }

      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser.user?.id) {
        throw new Error('Cannot submit review: user not authenticated');
      }

      const { error } = await supabase
        .from('reviews')
        .insert([{
          job_id: jobId,
          reviewee_user_id: revieweeUserId,
          reviewer_type: reviewerType,
          organization_id: organizationId,
          reviewer_user_id: currentUser.user.id,
          ...reviewData
        }]);

      if (error) throw error;

      // Automatically try to publish reviews if both parties have submitted
      try {
        await invokeEdgeFunction('publish-reviews', {
          callerOperation: 'useReviews.submitReview',
        });
      } catch (publishError) {
        console.log('Note: Could not auto-publish reviews:', publishError);
        // Don't fail the review submission if publishing fails
      }

      // Track review submission
      analytics.track({
        name: ANALYTICS_EVENTS.REVIEW_SUBMITTED,
        category: 'engagement',
        properties: {
          job_id: jobId,
          reviewer_type: reviewerType,
          overall_rating: reviewData.overall_rating,
        },
      });

      toast({
        title: "Review Submitted",
        description: "Your review has been submitted and will be published when both parties complete their reviews."
      });

      return true;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to submit review";
      console.error('Error submitting review:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [toast]);

  /**
   * Fetch all reviews for a specific job
   */
  const getJobReviews = useCallback(async (jobId: string): Promise<Review[]> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as DatabaseReview[])?.map(convertDatabaseReview) || [];
    } catch (error) {
      console.error('Error fetching job reviews:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch all published reviews for a specific user
   * Uses in-flight deduplication to prevent concurrent duplicate requests
   */
  const getUserReviews = useCallback(async (userId: string): Promise<Review[]> => {
    // Check if there's already an in-flight request for this user
    const existing = inFlightUserReviews.get(userId);
    if (existing) {
      return existing;
    }

    const fetchPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('reviews')
          .select('*')
          .eq('reviewee_user_id', userId)
          .eq('review_status', 'published')
          .order('published_at', { ascending: false });

        if (error) throw error;
        return (data as DatabaseReview[])?.map(convertDatabaseReview) || [];
      } catch (error) {
        console.error('Error fetching user reviews:', error);
        return [];
      } finally {
        // Clear from cache after completion
        inFlightUserReviews.delete(userId);
      }
    })();

    inFlightUserReviews.set(userId, fetchPromise);
    return fetchPromise;
  }, []);

  /**
   * Fetch aggregate review statistics for a user
   * Uses in-flight deduplication to prevent concurrent duplicate requests
   */
  const getUserReviewAggregate = useCallback(async (userId: string): Promise<UserReviewAggregate | null> => {
    // Check if there's already an in-flight request for this user
    const existing = inFlightUserAggregates.get(userId);
    if (existing) {
      return existing;
    }

    const fetchPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('user_review_aggregates')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') throw error;
        return data || null;
      } catch (error) {
        console.error('Error fetching user review aggregate:', error);
        return null;
      } finally {
        // Clear from cache after completion
        inFlightUserAggregates.delete(userId);
      }
    })();

    inFlightUserAggregates.set(userId, fetchPromise);
    return fetchPromise;
  }, []);

  /**
   * Update an existing review
   */
  const updateReview = useCallback(async (reviewId: string, reviewData: Partial<ReviewSubmission>): Promise<boolean> => {
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .update(reviewData)
        .eq('id', reviewId);

      if (error) throw error;

      toast({
        title: "Review Updated",
        description: "Your review has been updated successfully."
      });

      return true;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update review";
      console.error('Error updating review:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [toast]);

  /**
   * Check for reviews awaiting counterpart submission
   */
  const checkPendingReviews = useCallback(async (): Promise<Review[]> => {
    try {
      const currentUser = await supabase.auth.getUser();
      const userId = currentUser.data.user?.id;
      
      if (!userId) {
        return [];
      }
      
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('reviewer_user_id', userId)
        .eq('review_status', 'pending_reveal')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as DatabaseReview[])?.map(convertDatabaseReview) || [];
    } catch (error) {
      console.error('Error checking pending reviews:', error);
      return [];
    }
  }, []);

  // Create stable function references object - DO NOT include loading/submitting
  // as they change during fetches and would destabilize all function references
  const stableFunctions = useMemo(() => ({
    canUserReviewJob,
    submitReview,
    getJobReviews,
    getUserReviews,
    getUserReviewAggregate,
    updateReview,
    checkPendingReviews
  }), [
    canUserReviewJob,
    submitReview,
    getJobReviews,
    getUserReviews,
    getUserReviewAggregate,
    updateReview,
    checkPendingReviews
  ]);

  // Return stable functions + current state values (not memoized together)
  return {
    loading,
    submitting,
    ...stableFunctions
  };
};
